import { useState, useMemo, useCallback, useEffect } from 'react';
import type { TimeSlot, ScheduleData } from '../../Utils/scheduleUtils';
import { getSlotsForDate, toISODateString, getISOWeekAndYear, isSlotBusy } from '../../Utils/scheduleUtils';
import { useUserAgenda } from './useUserAgenda';
import { AgendaStatuses } from '../../ConfigFiles/agenda_status';

export interface ProcessedDayColumn {
  date: Date;
  isoDate: string;
  dayName: string;
  dayNumber: number;
  monthName: string;
  isToday: boolean;
  generatedSlots: TimeSlot[];
  isWorkingDay: boolean;
}

export function useSlotPicker(
  username?: string,
  earliestAvailability?: string | Date | null,
  schedule?: ScheduleData | null,
  maxMonths: number = 3,
  locale: string = 'en-US',
  hideNonWorkingDays: boolean = true
) {
  const [baseDate, setBaseDate] = useState<Date>(() => new Date());

  // Use the agenda hook instead of raw service/fetch
  const { fetchWeeklySchedule, isLoading, error } = useUserAgenda();
  const [busyEntries, setBusyEntries] = useState<any[]>([]);

  // Get start of week
  const startOfWeek = useMemo(() => {
    const d = new Date(baseDate);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [baseDate]);

  useEffect(() => {
  if (!username) {
    setBusyEntries([]);
    return;
  }

  let isMounted = true;
  const { year, weekNumber } = getISOWeekAndYear(startOfWeek);

  fetchWeeklySchedule(username, year, weekNumber)
    .then((data) => {
      if (!isMounted || !data) return;

      // 1. Safely extract raw array items
      // If `data` is an array, use it directly.
      // If `data.entries` is an array property, use it. Otherwise, fallback to empty array.
      const rawList = Array.isArray(data)
        ? data
        : Array.isArray((data as any).entries)
        ? (data as any).entries
        : [];

      // 2. Filter out CANCELLED entries so cancelled slots remain available
      const activeBusyEntries = rawList.filter(
        (entry: any) => entry.status !== AgendaStatuses.CANCELLED
      );

      setBusyEntries(activeBusyEntries);
    })
    .catch((err) => {
      console.error("Error loading slot availability:", err);
      if (isMounted) setBusyEntries([]);
    });

  return () => {
    isMounted = false;
  };
}, [username, startOfWeek, fetchWeeklySchedule]);

  const daysColumns = useMemo<ProcessedDayColumn[]>(() => {
    const todayStr = new Date().toDateString();
    const officeHours = schedule?.office_hours || {};
    const columns: ProcessedDayColumn[] = [];

    for (let i = 0; i < 7; i++) {
      const colDate = new Date(startOfWeek);
      colDate.setDate(startOfWeek.getDate() + i);

      const dayOfWeekKey = colDate.getDay().toString();
      const dayOfficeHours = officeHours[dayOfWeekKey] || [];
      const hasConfiguredHours = dayOfficeHours.length > 0;

      // 1. Generate base slots from office hours
      const rawSlots = schedule ? getSlotsForDate(colDate, schedule) : [];

      // 2. Mark slots overlapping with fetched busy entries
      const evaluatedSlots: TimeSlot[] = rawSlots.map((slot) => {
        const busy = isSlotBusy(slot.start, slot.end, busyEntries);
        return {
          ...slot,
          status: busy ? 'BUSY' : 'AVAILABLE',
        };
      });

      const isWorkingDay = hasConfiguredHours && evaluatedSlots.length > 0;

      if (hideNonWorkingDays && !isWorkingDay) {
        continue;
      }

      columns.push({
        date: colDate,
        isoDate: toISODateString(colDate),
        dayName: colDate.toLocaleDateString(locale, { weekday: 'short' }),
        dayNumber: colDate.getDate(),
        monthName: colDate.toLocaleDateString(locale, { month: 'short' }),
        isToday: colDate.toDateString() === todayStr,
        generatedSlots: evaluatedSlots,
        isWorkingDay,
      });
    }

    return columns;
  }, [startOfWeek, schedule, busyEntries, hideNonWorkingDays, locale]);

  const handleNextWeek = useCallback(() => {
    setBaseDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 7);
      return next;
    });
  }, []);

  const handlePrevWeek = useCallback(() => {
    setBaseDate((prev) => {
      const prevDate = new Date(prev);
      prevDate.setDate(prevDate.getDate() - 7);
      return prevDate;
    });
  }, []);

  const handleSelectSpecificDate = useCallback((date: Date) => {
    setBaseDate(date);
  }, []);

  const isPrevDisabled = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return startOfWeek <= today;
  }, [startOfWeek]);

  const isNextDisabled = useMemo(() => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + maxMonths);
    return startOfWeek >= maxDate;
  }, [startOfWeek, maxMonths]);

  return {
    baseDate,
    daysColumns,
    loading: isLoading,
    error,
    isPrevDisabled,
    isNextDisabled,
    handleNextDays: handleNextWeek,
    handlePrevDays: handlePrevWeek,
    handleSelectSpecificDate,
  };
}
